import * as SDK from '@replai-platform/sdk';
import { camelCaseToCapitalCase, MultiSelectDropDownOption } from '@replai-platform/ui-components';
import { api } from '../../../api';
import { FiltersState } from '../../../store/filters';
import { TAG_TYPE_GROUP_OPTION_ID_PREFIX } from '../../../utils/constants';

/**
 * Serializes a tag into a dropdown option id. The store keeps the parsed tag, so the id has to
 * round-trip through `JSON.parse`/`JSON.stringify` unchanged for the option to show up as selected
 * - which means the key order below must not change.
 */
export const serializeExcludedTag = (tag: Pick<SDK.Tag, 'type' | 'value'>) =>
  JSON.stringify({ type: tag.type, value: tag.value });

/**
 * Builds the options of the 'Exclude Tags' filter - the mirror image of the 'Select Tags' filter, so
 * it offers the same tag set (every type except the ones the project excludes) grouped by tag type,
 * except that here a selected option means "exclude this tag".
 *
 * Deliberately has no 'All' option: excluding every tag at once would just empty the table, while
 * making the heaviest possible request. Bulk selection is instead done a type at a time, through the
 * group headers.
 */
export async function fetchExcludedTags(
  projectId: SDK.UUID,
  currentFiltering: FiltersState,
  excludedTagTypes: string[] = []
) {
  const tagTypes = await api.tags.getTypes({
    projectIds: [projectId],
    tagsFilters: { tagTypesToExclude: excludedTagTypes.map((type) => ({ type })) },
  });

  const isExcluded = (tag: Pick<SDK.Tag, 'type' | 'value'>) =>
    !!currentFiltering.excludedTags?.some(({ type, value }) => type === tag.type && value === tag.value);

  // `tagTypesToExclude` is re-applied client side so a backend that ignores it cannot put a type the
  // project hides back in front of the user
  const hiddenTypes = new Set(excludedTagTypes);

  return tagTypes
    .filter(({ type }) => type && !hiddenTypes.has(type))
    .sort((a, b) => a.type.localeCompare(b.type))
    .flatMap(({ type, values }) => [
      {
        id: `${TAG_TYPE_GROUP_OPTION_ID_PREFIX}${type}`,
        label: camelCaseToCapitalCase(type),
        isGroupOption: true,
        groupId: type,
      },
      ...[...(values ?? [])]
        .sort((a, b) => (a ?? '').localeCompare(b ?? ''))
        .map((value) => ({
          id: serializeExcludedTag({ type, value }),
          label: value || SDK.messages.NOT_AVAILABLE,
          type: 'option',
          groupId: type,
          selected: isExcluded({ type, value }),
        })),
    ]) as MultiSelectDropDownOption[];
}
